import { isNumber } from "lodash";
import sortBy from "lodash/sortBy";
import { differenceInMinutes } from "date-fns";

import { AlertMarkerLocation, AlertType } from "@/components/common/GMap/types";
import { CategoryFilter } from "@/components/filters/CategoriesFilter/types";

import { DeviceType } from "@/features/alerts/types";
import { SummarizedAlert } from "@/features/alerts/types";
import {
  LocationSource,
  MIN_DWELL_DURATION_MS,
} from "@/features/trips/constants";
import { DwellLocation, Location, Metric } from "@/features/types";

import { DateUtils } from "@/utils/date";

import { MapFeatureLocation } from "./system/components/MapFeaturesMap/types";
import { StandbyProvisionedTimeBounds } from "./system/components/StandbySensors/types";
import {
  AnchorPoint,
  DEVICE_TYPE_TO_NAME,
  Geofence,
  MapFeatureType,
  Place,
  UserRoleKey,
  UserStatusKey,
} from "./system/types";

export const filterBrokenLocations = (locations: Location[]) => {
  return locations.filter((location) => !!location.latlng);
};

export const LOCATION_PROXIMITY_THRESHOLD_IN_MINUTES = 24 * 60;

type ProtoMarker = {
  alertName: string;
  chorusAlertId: string;
  time: string;
  type: AlertType;
};
const sortAlertMarkerLocations = (markers: AlertMarkerLocation[]) =>
  markers.toSorted(
    (a, b) => new Date(b.alertTime).getTime() - new Date(a.alertTime).getTime(),
  );

export const computeAlertMarkerLocations = (
  locations?: Location[],
  summarizedAlerts?: SummarizedAlert[],
  triggerLocation?: Location,
): AlertMarkerLocation[] => {
  // Markers are rendered as a set, so an explicit trigger location still waits
  // for the location history that the other markers are derived from.
  if (!locations?.length || !summarizedAlerts?.length) {
    return [];
  }

  const protoMarkers = sortBy(extractProtoMarkers(summarizedAlerts), "time");
  const explicitIncidentMarkers: AlertMarkerLocation[] = triggerLocation
    ? protoMarkers
        .filter((proto) => proto.type === "incident")
        .map((proto) => ({
          alertName: proto.alertName,
          chorusAlertId: proto.chorusAlertId,
          alertTime: proto.time,
          type: proto.type,
          location: triggerLocation,
        }))
    : [];
  const protoMarkersToDerive = triggerLocation
    ? protoMarkers.filter((proto) => proto.type !== "incident")
    : protoMarkers;

  const sortedLocations = sortBy(locations, "locationTime");

  let locationIndex = 0;
  let lastLocation: Location | null = null;
  let lastDiff: number | null = null;
  const derivedMarkers = protoMarkersToDerive
    .map((protoElement) => {
      if (!isNumber(lastDiff) && lastLocation) {
        if (isDwellLocation(lastLocation)) {
          lastDiff = getDwellDiffInMinutes(
            protoElement.time,
            lastLocation.startTime,
            lastLocation.endTime,
          );
        } else {
          lastDiff = getDiffInMinutes(
            new Date(protoElement.time),
            lastLocation.locationTime,
          );
        }
      }
      while (locationIndex < sortedLocations.length) {
        const location = sortedLocations[locationIndex];
        let diff: number;
        if (isDwellLocation(location)) {
          diff = getDwellDiffInMinutes(
            protoElement.time,
            location.startTime,
            location.endTime,
          );
        } else {
          diff = getDiffInMinutes(
            new Date(protoElement.time),
            location.locationTime,
          );
        }
        if (isNumber(lastDiff) && lastDiff < diff) {
          lastDiff = null;
          return buildReturnValue(protoElement, lastLocation as Location);
        }
        lastDiff = diff;
        lastLocation = location;
        locationIndex++;
      }
      return buildReturnValue(protoElement, lastLocation as Location);
    })
    .filter((element) => element) as AlertMarkerLocation[];

  return sortAlertMarkerLocations([
    ...derivedMarkers,
    ...explicitIncidentMarkers,
  ]);
};

/**
 * Removes incident markers that share coordinates with a resolution marker
 * for the same alert, so that only the resolution marker gets drawn on the map.
 */
export const filterOverlappingIncidents = (
  alertMarkerLocations: AlertMarkerLocation[],
): AlertMarkerLocation[] =>
  alertMarkerLocations.filter(
    (a) =>
      a.type === "resolution" ||
      (a.type === "incident" &&
        !alertMarkerLocations.find(
          (b) =>
            b.chorusAlertId === a.chorusAlertId &&
            b.type === "resolution" &&
            b.location.latlng.latitudeMicro ===
              a.location.latlng.latitudeMicro &&
            b.location.latlng.longitudeMicro ===
              a.location.latlng.longitudeMicro,
        )),
  );

export const findMetricForTimestamp = (metrics: Metric[], targetDate: Date) => {
  let lastDiff = null;
  let bestDiff = Infinity;
  let bestMatch = null;

  for (const metric of metrics) {
    const diff = getDiffInMinutes(targetDate, metric.metricTime);

    if (lastDiff && lastDiff < diff) {
      break;
    }
    if (diff < bestDiff) {
      bestDiff = diff;
      bestMatch = metric;
    }
    lastDiff = diff;
  }
  return bestMatch;
};
export const findLocationForTimestamp = (
  locations: (Location | DwellLocation)[],
  targetDate: Date,
) => {
  let lastDiff = null;
  let bestDiff = Infinity;
  let bestMatch = null;
  for (const location of locations) {
    let diff = getDiffInMinutes(targetDate, location.locationTime);

    // If `location` is of type `DwellLocation`, we want to use the start/end
    // times as a potential best match.
    if (isDwellLocation(location)) {
      diff = Math.min(
        diff,
        getDiffInMinutes(targetDate, location.startTime),
        getDiffInMinutes(targetDate, location.endTime),
      );
    }

    if (lastDiff && lastDiff < diff) {
      break;
    }
    if (bestDiff > diff) {
      bestDiff = diff;
      bestMatch = location;
    }
    lastDiff = diff;
  }

  return bestMatch;
};

const getDiffInMinutes = (date1: Date, date2: string) => {
  return Math.abs(differenceInMinutes(date1, new Date(date2)));
};

const getDwellDiffInMinutes = (
  alertTime: string,
  startTime: string,
  endTime: string,
) => {
  return Math.min(
    Math.abs(differenceInMinutes(new Date(alertTime), new Date(startTime))),
    Math.abs(differenceInMinutes(new Date(alertTime), new Date(endTime))),
  );
};

const extractProtoMarkers = (
  summarizedAlerts: SummarizedAlert[],
): ProtoMarker[] => {
  return summarizedAlerts.flatMap((alert) => {
    const elements: ProtoMarker[] = [];
    elements.push({
      time: alert.incidentTime,
      alertName: alert.alertConfigName,
      chorusAlertId: alert.chorusAlertId,
      type: "incident",
    });

    if (alert.resolutionTime) {
      elements.push({
        time: alert.resolutionTime,
        alertName: alert.alertConfigName,
        chorusAlertId: alert.chorusAlertId,
        type: "resolution",
      });
    }

    return elements;
  });
};

const buildReturnValue = (protoMarker: ProtoMarker, location: Location) => {
  if (isDwellLocation(location)) {
    return getDwellDiffInMinutes(
      protoMarker.time,
      location.startTime,
      location.endTime,
    ) < LOCATION_PROXIMITY_THRESHOLD_IN_MINUTES
      ? {
          alertName: protoMarker.alertName,
          chorusAlertId: protoMarker.chorusAlertId,
          alertTime: protoMarker.time,
          type: protoMarker.type,
          location: location,
        }
      : null;
  }
  return getDiffInMinutes(new Date(protoMarker.time), location.locationTime) <
    LOCATION_PROXIMITY_THRESHOLD_IN_MINUTES
    ? {
        alertName: protoMarker.alertName,
        chorusAlertId: protoMarker.chorusAlertId,
        alertTime: protoMarker.time,
        type: protoMarker.type,
        location: location,
      }
    : null;
};

/**
 * Checks if a Location is a DwellLocation.
 * @param   {Location} location The Location object to check.
 * @returns {boolean}           Whether location is a DwellLocation.
 */
export function isDwellLocation(
  location: Location | DwellLocation,
): location is DwellLocation {
  return (location as DwellLocation).dwellTimeMs !== undefined;
}

/**
 * Checks if a Location is a "fixed" Location.
 * @param   {Location} location The Location object to check.
 * @returns {boolean}           Whether location is fixed.
 */
export function isFixedLocation(location: Location) {
  return location.locationSource === LocationSource.Fixed;
}

/**
 * Calculate the difference between two times
 * @param  {string} startTime The Location object for the initial time.
 * @param  {string} endTime   The Location object for the end time.
 * @return {number}           The elapsed time in milliseconds
 */
export function timeDifferenceMs(
  startTime: string | Date,
  endTime: string | Date,
): number {
  const start = new Date(startTime);
  const end = new Date(endTime);
  return Math.abs(end.getTime() - start.getTime());
}

/**
 * Merges a start and end location for a string of locations with the same
 * latLng into either a DwellLocation object or Location depending on whether
 * the time diff is more or less than MIN_DWELL_DURATION_MS.
 * @param  {Location} locationStart   The Location object for the initial time.
 * @param  {Location} locationEnd     The Location object for the end time.
 * @param  {number}   largestAccuracy The accuracy to use for the result.
 * @return {Location | DwellLocation} A single Location or DwellLocation.
 */
export function combineLocations(
  locationStart: Location,
  locationEnd: Location,
  largestAccuracy: number,
  isFixed: boolean,
  placeName?: string,
  placeAddress?: string,
): Location | DwellLocation {
  const timeDiffMs = timeDifferenceMs(
    locationStart.locationTime,
    locationEnd.locationTime,
  );
  if (timeDiffMs < MIN_DWELL_DURATION_MS) {
    // return a regular Location rather than DwellLocation if dwell time is
    // less than MIN_DWELL_DURATION_MS
    locationStart.accuracyCentiMeters = largestAccuracy;
    return {
      // We use the locationEnd rather than locationStart (here and below) to set the locationTime
      // as the most recent time that the device was at a given latlng (see SWE-1602)
      ...locationEnd,
      accuracyCentiMeters: largestAccuracy,
      locationSource: isFixed
        ? LocationSource.Fixed
        : locationStart.locationSource,
      placeName: placeName,
      placeAddress: placeAddress,
    };
  }
  return {
    ...locationEnd,
    accuracyCentiMeters: largestAccuracy,
    startTime: locationStart.locationTime,
    endTime: locationEnd.locationTime,
    dwellTimeMs: timeDiffMs,
    locationSource: isFixed
      ? LocationSource.Fixed
      : locationStart.locationSource,
    placeName: placeName,
    placeAddress: placeAddress,
  };
}

export const getAttributeInQueryObject = (
  attribute: CategoryFilter,
  filter: CategoryFilter,
) => {
  if (!filter) {
    return {};
  }
  let keyName = "string_value";
  let value = attribute.value;
  const dataType = filter.dataType;
  const criteriaType = filter.criteria;
  if (dataType == "INTEGER") {
    keyName = "integer_value";
  } else if (dataType == "DECIMAL") {
    keyName = "decimal_value";
  } else if (dataType == "TIMESTAMP") {
    keyName = "timestamp_value";
    if (criteriaType == "CUSTOM_RANGE") {
      if (!value || !value.includes("->")) {
        return [
          {
            name: attribute.name,
            criteriaType: "GREATER_THAN_OR_EQUAL",
          },
          {
            name: attribute.name,
            criteriaType: "LESS_THAN_OR_EQUAL",
          },
        ];
      }

      let [fromdate, toDate] = value.split(" -> ").map((date) => date.trim());
      fromdate = isNaN(Date.parse(fromdate)) ? "" : fromdate;
      toDate = isNaN(Date.parse(toDate))
        ? ""
        : new Date(new Date(toDate).setHours(23, 59, 59, 999)).toISOString();

      return [
        {
          name: attribute.name,
          [keyName]: fromdate,
          criteriaType: "GREATER_THAN_OR_EQUAL",
        },
        {
          name: attribute.name,
          [keyName]: toDate,
          criteriaType: "LESS_THAN_OR_EQUAL",
        },
      ];
    } else {
      value = !value || isNaN(Date.parse(value)) ? undefined : value;
    }
  } else if (dataType == "STRING") {
    value = attribute.value?.split("|").join(",");
  }
  if (
    attribute.criteria == "EQUAL" ||
    attribute.criteria == "EXACT_DATE" ||
    attribute.criteria == "TEXT"
  ) {
    return {
      name: attribute.name,
      [keyName]: value,
    };
  } else {
    return {
      name: attribute.name,
      [keyName]: value,
      criteriaType:
        attribute.criteria == "AFTER_SPECIFIC_DATE"
          ? "GREATER_THAN_OR_EQUAL"
          : attribute.criteria == "BEFORE_SPECIFIC_DATE"
            ? "LESS_THAN_OR_EQUAL"
            : attribute.criteria,
    };
  }
};

export const findAssociatedPlaceForGeofence = (
  geofence: Geofence,
  places: Place[],
): Place | undefined => {
  if (places.length === 0) {
    return undefined;
  }
  return places.find(
    (place) =>
      place.pointAndRadius.point.latitudeMicro ===
        geofence.center.latitudeMicro &&
      place.pointAndRadius.point.longitudeMicro ===
        geofence.center.longitudeMicro,
  );
};

export const getGeofenceDisplayRadiusMeters = (
  geofence: Geofence,
  places: Place[] = [],
): number => {
  return (
    geofence.radiusMeters +
    (findAssociatedPlaceForGeofence(geofence, places)?.pointAndRadius
      .radiusMeters ?? 0)
  );
};

export const convertGeofenceToMapFeatureObject = (
  geofences: Geofence[],
  places: Place[] = [],
): MapFeatureLocation[] => {
  const mapFeatureLocations: MapFeatureLocation[] = [];
  for (const geofence of geofences) {
    const associatedPlace = findAssociatedPlaceForGeofence(geofence, places);
    const placeRadiusMeters = associatedPlace?.pointAndRadius.radiusMeters;

    mapFeatureLocations.push({
      type: MapFeatureType.GEO_FENCE,
      location: {
        latlng: {
          latitudeMicro: geofence.center.latitudeMicro,
          longitudeMicro: geofence.center.longitudeMicro,
        },
        accuracyCentiMeters:
          getGeofenceDisplayRadiusMeters(geofence, places) * 100,
        locationTime: Date.now().toString(),
        placeName: geofence.placeName,
        ...(associatedPlace?.formattedStreetAddress
          ? { placeAddress: associatedPlace.formattedStreetAddress }
          : {}),
        ...(placeRadiusMeters != null ? { placeRadiusMeters } : {}),
        placeAddress:
          associatedPlace?.formattedStreetAddress ??
          geofence.formattedStreetAddress,
        placeRadiusMeters,
        eventBufferMeters: geofence.radiusMeters,
      },
      name: geofence.eventRuleName,
      mapFeatureId: geofence.eventRuleId,
    });
  }
  return mapFeatureLocations;
};

export const convertPlaceToMapFeatureObject = (
  places: Place[],
): MapFeatureLocation[] => {
  const mapFeatureLocations: MapFeatureLocation[] = [];
  for (const place of places) {
    mapFeatureLocations.push({
      type: MapFeatureType.PLACE,
      location: {
        latlng: {
          latitudeMicro: place.pointAndRadius.point.latitudeMicro,
          longitudeMicro: place.pointAndRadius.point.longitudeMicro,
        },
        accuracyCentiMeters: place.pointAndRadius.radiusMeters * 100,
        placeName: place.customerName,
        placeAddress: place.formattedStreetAddress,
        locationTime: Date.now().toString(),
      },
      name: place.customerName,
      mapFeatureId: place.placeId,
    });
  }
  return mapFeatureLocations;
};

export const convertAnchorPointToMapFeatureObject = (
  anchorPoints: AnchorPoint[],
): MapFeatureLocation[] => {
  const mapFeatureLocations: MapFeatureLocation[] = [];
  for (const anchorPoint of anchorPoints) {
    mapFeatureLocations.push({
      type: MapFeatureType.ANCHOR_POINT,
      location: {
        latlng: {
          latitudeMicro: anchorPoint.location.latlng.latitudeMicro,
          longitudeMicro: anchorPoint.location.latlng.longitudeMicro,
        },
        accuracyCentiMeters: anchorPoint.location.floorOffsetCm,
        placeName: anchorPoint.placeName,
        placeAddress: anchorPoint.formattedStreetAddress,
        locationTime: Date.now().toString(),
      },
      name: anchorPoint.name,
      mapFeatureId: anchorPoint.anchorPointId,
    });
  }
  return mapFeatureLocations;
};

export function getExpandedBounds(
  bounds: google.maps.LatLngBounds,
  padding = 0.2,
) {
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();

  const latDiff = ne.lat() - sw.lat();
  const lngDiff = ne.lng() - sw.lng();
  const lngSpan = lngDiff < 0 ? ne.lng() + 360 - sw.lng() : lngDiff;

  const expandedSwLng = sw.lng() - lngSpan * padding;
  const expandedNeLng = ne.lng() + lngSpan * padding;
  const clampLng = (lng: number) => Math.max(-180, Math.min(180, lng));

  const expandedSw = new google.maps.LatLng(
    sw.lat() - latDiff * padding,
    clampLng(expandedSwLng),
  );

  const expandedNe = new google.maps.LatLng(
    ne.lat() + latDiff * padding,
    clampLng(expandedNeLng),
  );

  return new google.maps.LatLngBounds(expandedSw, expandedNe);
}

export const normalizeUserStatus = (status: string): UserStatusKey => {
  if (status === "DELETED") {
    return "DEACTIVATED";
  }
  return status as UserStatusKey;
};

/**
 * Checks if a user has the required role to access a feature.
 * @param   {UserRoleKey | "ADMIN"} userRole      The user's role from the current user data.
 * @param   {UserRoleKey | "ADMIN"} requiredRole  The required role for the feature.
 * @returns {boolean}              Whether the user has the required role.
 */
export const hasRequiredRole = (
  userRole: (UserRoleKey | "ADMIN") | undefined,
  requiredRole: UserRoleKey | "ADMIN",
): boolean => {
  if (!userRole) {
    return false;
  }

  if (requiredRole === "ADMIN") {
    return userRole === "ADMIN";
  }

  if (requiredRole === "MANAGER") {
    return userRole === "MANAGER" || userRole === "ADMIN";
  }

  return false;
};

export const groupDeviceTypesByName = (
  availableDeviceTypes: DeviceType[],
  getDeviceTypeMessage: (deviceType: DeviceType) => string,
): Map<string, { displayName: string; deviceTypes: DeviceType[] }> => {
  const map = new Map<
    string,
    { displayName: string; deviceTypes: DeviceType[] }
  >();
  availableDeviceTypes.forEach((deviceType) => {
    const displayName = getDeviceTypeMessage(deviceType);
    // A device type the API returns before it is added to DEVICE_TYPE_TO_NAME
    // has no base name, so it groups under itself rather than collapsing every
    // unmapped type into one `undefined` group.
    const baseName: string = DEVICE_TYPE_TO_NAME[deviceType] ?? deviceType;
    if (!map.has(baseName)) {
      map.set(baseName, { displayName, deviceTypes: [] });
    }
    map.get(baseName)!.deviceTypes.push(deviceType);
  });
  return map;
};

export const getAllDeviceTypesWithBaseName = (
  baseName: string,
): DeviceType[] => {
  const deviceTypes: DeviceType[] = [];
  (Object.keys(DEVICE_TYPE_TO_NAME) as DeviceType[]).forEach((deviceType) => {
    if (DEVICE_TYPE_TO_NAME[deviceType] === baseName) {
      deviceTypes.push(deviceType);
    }
  });
  // Unmapped device types are grouped under themselves, so the base name is the
  // device type itself.
  if (deviceTypes.length === 0 && baseName) {
    return [baseName as DeviceType];
  }
  return deviceTypes;
};

/**
 * Standby-specific wrapper around {@link DateUtils.getCalendarDayBounds}.
 * Maps generic `startTime`/`endTime` to the `provisionedAfterTime`/`provisionedBeforeTime`
 * field names expected by the standby device APIs.
 */
export function standbyProvisionBoundsFromCalendarDay(
  dateKey: string | Date,
  timeZone: string,
): StandbyProvisionedTimeBounds | null {
  const bounds = DateUtils.getCalendarDayBounds(dateKey, timeZone);
  if (!bounds) return null;
  return {
    provisionedAfterTime: bounds.startTime,
    provisionedBeforeTime: bounds.endTime,
  };
}
